import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Icon } from '@aqarmap/aqarmap-ui';
import Modal from '~/components/Modal';
import { Languages } from '~/constants/enums';
import { UpdatePhotos } from '~myAccount/enums';
import { useAppDispatch } from '~/redux/hooks';
import { closeModal, openModal } from '~/redux/features/uiState/uiStateSlice';
import { useRemovePhotoMutation } from '~/redux/api/userApiSlice';
import { objectSerialize } from '~/lib';

const ChangeImage = ({
  imgSrc,
  getImageFile,
  type,
  title,
}: {
  imgSrc: string;
  getImageFile: (file: File) => void;
  type: UpdatePhotos;
  title: string;
}) => {
  const dispatch = useAppDispatch();

  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const { t, i18n } = useTranslation('app');
  const locale = i18n.language;

  const inputRef = useRef<HTMLInputElement>(null);

  const [removePhoto, { isLoading, isSuccess }] = useRemovePhotoMutation();

  const handleCameraClick = () => {
    inputRef.current?.click();
  };

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = event.target.files as FileList;
    const image = files[0] as File;
    if (image.type.startsWith('image')) {
      getImageFile(image);
      setSelectedImage(URL.createObjectURL(image as File));
      dispatch(closeModal('changePhoto'));
    }
  };

  const handleRemove = async () => {
    const formData = objectSerialize({ type });
    await removePhoto(formData);
  };

  useEffect(() => {
    if (isSuccess) {
      dispatch(closeModal('changePhoto'));
    }
  }, [isSuccess]);

  return (
    <>
      <button
        type="button"
        className="relative cursor-pointer w-[120px] h-[120px] rounded-full object-contain border border-gray element-center"
        onClick={() => dispatch(openModal('changePhoto'))}
      >
        <img
          src={selectedImage || imgSrc}
          alt="Account-Img"
          className="w-[120px] h-[120px] rounded-full object-cover border border-gray"
        />
        <span
          className={`absolute ${locale === Languages.ENGLISH ? 'left-0' : 'right-0'} bottom-0 bg-white w-[32px] h-[32px] border border-gray rounded-full element-center`}
        >
          <Icon name="camera" className="text-gray__dark_1" />
        </span>
      </button>

      <Modal modalKey="changePhoto" title={title}>
        <div className="w-full flex flex-col gap-2x pb-3x">
          <div className="relative cursor-pointer" onClick={handleCameraClick}>
            <input
              ref={inputRef}
              type="file"
              title="upload-img"
              onChange={handleImageUpload}
              className="absolute -z-10 inset-0 w-full h-full opacity-0 rounded-full cursor-pointer"
              aria-label="Choose file"
              accept="image/*"
            />
            <Button
              size="small"
              ariaLabel="new-img"
              variant="secondary"
              className="w-full h-10"
            >
              <Icon name="add-photo" className="text-blue" />
              {t('newPhoto')}
            </Button>
          </div>
          <Button
            size="small"
            type="button"
            onClick={handleRemove}
            ariaLabel="delete"
            disabled={isLoading}
            variant="secondary"
            className="w-full h-10 text-red_dark"
            Icon={<Icon name="trash" className="text-red_dark" />}
          >
            {t('delete')}
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default ChangeImage;
